import { FusionCustomInput } from 'components/index';
import SearchBar from 'components/SearchBar/SearchBar';
import useSearch from 'hooks/useSearch';
import { useEffect, useState } from 'react';
import { Button, Container, Form } from 'react-bootstrap';
import { FaSave } from 'react-icons/fa';
import { FaFloppyDisk, FaPencil } from 'react-icons/fa6';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import {
  useGetOrgNameQuery,
  useUpdateOrgNameMutation,
} from 'services/organization/organization.api';
import { setUserOrganization } from 'store/slices/User/userSlice';
import { urlAppendParam } from 'utils/navigation/tabsHeader.utils';
import UserTable from '../../../features/Admin/Users/UserTable/UserTable';

export default function MembersTab({
  perms,
  onCreateUser,
  listOnly,
  editableOrgName,
  hideCreateUserButton = false,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const search = useSearch();
  const [editOrgName, setEditOrgName] = useState(false);
  const [localOrgName, setLocalOrgName] = useState('');
  const { data: orgName = '' } = useGetOrgNameQuery();
  const [updateOrgName] = useUpdateOrgNameMutation();
  const hasEditOrgNamePerm = perms?.testPermission(
    [8, 9, 10, 11, 12, 13],
    ['EditOrganizationName'],
  );

  useEffect(() => {
    if (localOrgName !== orgName) {
      setLocalOrgName(orgName);
    }
  }, [orgName]);

  const localUpdateOrgName = async () => {
    try {
      await updateOrgName(localOrgName).unwrap();
      dispatch(setUserOrganization(localOrgName));
    } catch (error) {
      console.error('Failed to update organization name');
    } finally {
      setEditOrgName(false);
    }
  };

  const handleView = (id) => navigate(`/system/viewuser?userid=${id}`);
  const handleEdit = (id) => navigate(`/system/edituser?userid=${id}`);

  return (
    <Container
      fluid
      className="mt-3 pb-3 d-flex flex-column flex-grow-1 h-100 min-h-0 border-box"
      style={{ boxSizing: 'border-box' }}
    >
      <div className="d-flex flex-column flex-md-row align-items-center gap-2 justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          {editableOrgName && hasEditOrgNamePerm ? (
            <div className="d-flex gap-1">
              <FusionCustomInput className="bg-transparent">
                <Form.Control
                  className="border-0 bg-transparent"
                  disabled={!editOrgName}
                  value={localOrgName}
                  onChange={(e) => setLocalOrgName(e.target.value)}
                />
              </FusionCustomInput>
              {editOrgName ? (
                <Button
                  onClick={() => {
                    localUpdateOrgName();
                  }}
                >
                  <FaFloppyDisk />
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    setEditOrgName(true);
                  }}
                >
                  <FaPencil />
                </Button>
              )}
            </div>
          ) : (
            <p className="fw-bold fs-6 text-white mb-0">{orgName || localOrgName}</p> // displays when lacking perms
          )}
        </div>
        <div className="ms-md-auto">
          <SearchBar className="bg-primary-highlight rounded-3" />
        </div>
      </div>
      <div
        className="w-100 flex-grow-1 overflow-hidden d-flex flex-column"
        style={{ minHeight: 0 }}
      >
        <UserTable perms={perms} onView={handleView} onEdit={handleEdit} searchTerm={search} />
      </div>
      {!hideCreateUserButton &&
        !listOnly &&
        perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditUsers']) && (
          <div className="d-flex flex-row justify-content-end">
            <Button
              as={Link}
              to={urlAppendParam('tab', 'Create New User')}
              className="p-3 w-100 mt-3 w-md-auto"
              onClick={onCreateUser}
              variant="primary"
            >
              Create New User
            </Button>
          </div>
        )}
    </Container>
  );
}
